import React, { useState, useRef, useEffect } from 'react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Send, CheckCircle, AlertCircle, MessageCircle, Key, Phone, Zap } from 'lucide-react';
import favicon from '../assets/favicon.png';

export default function SendWhatsAppMessage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [apiKey, setApiKey] = useState('');
  
  useEffect(() => {
    const savedKey = localStorage.getItem('otp_api_key');
    if (savedKey) setApiKey(savedKey);
  }, []);

  const handleApiKeyChange = (e) => {
    setApiKey(e.target.value);
    localStorage.setItem('otp_api_key', e.target.value);
  };

  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setError(null);
    
    let msgToSend = message;
    if (msgToSend && !msgToSend.includes('{otp}')) {
      msgToSend = msgToSend.trim() + ' {otp}';
    }
    
    try {
      const res = await axios.post(
        '/api/v1/send-otp',
        { phone, otp, message: msgToSend },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
          },
        }
      );
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#0B0E14] p-4 sm:p-6 md:p-8">
        {/* Header */}
        <div style={{direction: isRTL ? 'rtl' : 'ltr'}} className="mb-6 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3">
            ✉️ {t('Try Sending OTP')}
          </h1>
          <p className="text-xs sm:text-base md:text-lg text-[#A0AEC0]">{t('Test your WhatsApp integration with a real message')}</p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8 ${isRTL ? 'text-right' : ''}`}>
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl p-4 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                
                {/* API Key Input */}
                <div className="space-y-2">
                  <label className={`text-[#A0AEC0] text-xs sm:text-sm font-semibold flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Key size={14} className="sm:w-4 sm:h-4 text-[#128C7E]" />
                    {t('API Key')}
                  </label>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={handleApiKeyChange}
                    placeholder={t('Paste your API key here')}
                    className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors font-mono"
                    required
                  />
                  <p className="text-xs text-[#A0AEC0]/70">{t('Get your API key from the dashboard')}</p>
                </div>

                {/* Phone Input */}
                <div className="space-y-2">
                  <label className={`text-[#A0AEC0] text-xs sm:text-sm font-semibold flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Phone size={14} className="sm:w-4 sm:h-4 text-[#128C7E]" />
                    {t('Phone Number')}
                  </label>
                  <input
                    type="text"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder={t('e.g. 962791901035')}
                    className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors"
                    required
                  />
                </div>

                {/* OTP Input */}
                <div className="space-y-2">
                  <label className={`text-[#A0AEC0] text-xs sm:text-sm font-semibold flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Zap size={14} className="sm:w-4 sm:h-4 text-[#128C7E]" />
                    {t('OTP Code')}
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={e => setOtp(e.target.value)}
                    placeholder={t('e.g. 4850')}
                    className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors font-mono"
                    required
                  />
                </div>

                {/* Message Input */}
                <div className="space-y-2">
                  <label className={`text-[#A0AEC0] text-xs sm:text-sm font-semibold flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <MessageCircle size={14} className="sm:w-4 sm:h-4 text-[#128C7E]" />
                    {t('Message')}
                  </label>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder={t('Your OTP is {otp}. Please enter it within 10 minutes.')}
                    className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors resize-none"
                    rows="4"
                    required
                  />
                  <p className="text-xs text-[#A0AEC0]/70">{t('Use {otp} placeholder for the code')}</p>
                </div>

                {/* Error Alert */}
                {error && (
                  <div className={`bg-red-500/20 border border-red-500/30 text-red-400 px-4 sm:px-6 py-3 sm:py-4 rounded-lg sm:rounded-xl flex items-start gap-2 sm:gap-3 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <AlertCircle size={16} className="sm:w-4.5 sm:h-4.5 flex-shrink-0 mt-0.5" />
                    <div className={isRTL ? 'text-right' : ''}>
                      <p className="font-semibold mb-1">{t('Error sending message')}</p>
                      <p className="text-xs sm:text-sm">{error}</p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-2 sm:py-3 bg-[#128C7E] text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:bg-[#0DA699] transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <Send size={16} className="sm:w-4.5 sm:h-4.5" />
                  {loading ? t('Sending...') : t('Send Message')}
                </button>
              </form>
            </div>
          </div>

          {/* Preview Section */}
          <div className={`lg:col-span-1 ${isRTL ? 'text-right' : ''}`}>
            {/* WhatsApp Preview */}
            {result && result.success && (
              <div className="bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-2xl p-6 sticky top-6">
                <h3 className={`text-white font-bold mb-6 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle size={20} className="text-[#128C7E]" />
                  {t('Message Sent')}
                </h3>
                
                {/* WhatsApp Chat Simulation */}
                <div className="bg-[#0B0E14] rounded-2xl overflow-hidden">
                  {/* Chat Header */}
                  <div className={`bg-[#128C7E] p-4 flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <img src={favicon} alt="avatar" className="w-8 h-8 rounded-full" />
                    <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                      <p className="text-white font-semibold text-sm">Safrjal OTP</p>
                      <p className="text-[#128C7E]/70 text-xs">Online</p>
                    </div>
                  </div>

                  {/* Message Bubble */}
                  <div className="p-4 space-y-3">
                    <div className="flex justify-end">
                      <div className="bg-[#128C7E] text-white px-4 py-2 rounded-2xl rounded-tr-none max-w-xs text-sm break-words">
                        {message.trim()
                          ? (message.includes('{otp}')
                              ? message.replace('{otp}', otp)
                              : message + ' ' + otp)
                          : `OTP: ${otp}`}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[#A0AEC0] text-xs">
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ✓✓
                      </p>
                    </div>
                  </div>
                </div>

                {/* Message Info */}
                <div className="mt-6 space-y-3">
                  <div className="bg-[#0B0E14]/50 rounded-xl p-4">
                    <p className="text-[#A0AEC0] text-xs mb-1">{t('Recipient')}</p>
                    <p className="text-white font-mono font-semibold text-sm">{phone}</p>
                  </div>
                  <div className="bg-[#0B0E14]/50 rounded-xl p-4">
                    <p className="text-[#A0AEC0] text-xs mb-1">{t('OTP Code')}</p>
                    <p className="text-[#128C7E] font-mono font-bold text-lg">{otp}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tips Section */}
            {!result && (
              <div className="bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-2xl p-6 sticky top-6">
                <h3 className="text-white font-bold mb-4">💡 {t('Tips')}</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex gap-2 text-[#A0AEC0]">
                    <span className="text-[#128C7E] flex-shrink-0">1.</span>
                    <span>{t('Use the API key from your dashboard')}</span>
                  </li>
                  <li className="flex gap-2 text-[#A0AEC0]">
                    <span className="text-[#128C7E] flex-shrink-0">2.</span>
                    <span>{t('Phone number must be in international format')}</span>
                  </li>
                  <li className="flex gap-2 text-[#A0AEC0]">
                    <span className="text-[#128C7E] flex-shrink-0">3.</span>
                    <span>{t('Use {otp} placeholder in your message')}</span>
                  </li>
                  <li className="flex gap-2 text-[#A0AEC0]">
                    <span className="text-[#128C7E] flex-shrink-0">4.</span>
                    <span>{t('Message will be sent via WhatsApp')}</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
